import { useMemo } from 'react';

import { LicenseCoarseLevels, UserAccessBadge } from '../../../hooks/useAccessBadge';
import { useFeatureFlag } from '../../../hooks/useFeatureFlag';
import { GeneratorCoarseLicenseLevel } from '../../../services/TemplateService/Template.type';
import { notUndefined } from '../../../utils/general';
import { getConfig } from '../../../utils/store';
import { ConsumerGenerator, TestprepPlanIcon } from './TestPrepTab';
import { TestPrepVariantMap as tmap } from './TestPrepVariantMap';
interface QuestionType {
  id: string; // e.g. 'SCR', 'ECR', 'text_input', etc.
  displayName: string;
  grades: (string | number)[]; // All valid grades
  lessonVariants: LessonVariant[];
}
export interface DropdownOption {
  displayName: string;
  value: string;
  disabled?: boolean;
  displayDivider?: boolean;
  icon?: JSX.Element;
  isEnterprise?: boolean;
}

export interface Test {
  id: string; // e.g. 'STAAR_RLA'
  displayName: string; // e.g. 'RLA'
  testGroup: DropdownOption; // e.g. 'STAAR' or 'TELPAS'
  states: DropdownOption[]; // e.g. ['TX'], ['TX','CA'], etc.
  questionTypes: QuestionType[];
}

export type LessonVariant = {
  variant: string;
  displayName?: string;
  lessonId: string;
};

const config = getConfig();

export function useTestPrepTree(): Test[] {
  const isTestPrepTemplatesEnabled = useFeatureFlag('templated_customizable_lessons');
  if (isTestPrepTemplatesEnabled) {
    return templatedTests;
  }
  return tests;
}

const templatedTests: Test[] = [
  //*CALIFORNIA CAASPP
  {
    id: 'CAASPP',
    displayName: 'ELA',
    testGroup: { value: 'CAASPP', displayName: 'CAASPP' },
    states: [{ displayName: 'California', value: 'CA' }],
    questionTypes: [
      {
        id: 'SA',
        displayName: 'Short Answer',
        grades: [3, 4, 5, 6, 7, 8, 9, 10, 11, 12],
        lessonVariants: [
          {
            variant: 'informational', // maybe change this? idk
            displayName: 'Short Answer',
            lessonId: tmap['SCR Template'].variants['CA CAASPP ELA Short Answer'],
          },
          {
            variant: 'upload',
            displayName: 'Upload',
            lessonId:
              tmap['Curify SCR Template'].variants['CA CAASPP ELA Short Answer Curify'],
          },
        ],
      },
      {
        id: 'PP',
        displayName: 'Paired Passage Short Answer',
        grades: [3, 4, 5, 6, 7, 8, 9, 10, 11, 12],
        lessonVariants: [
          {
            variant: 'paired-passage',
            displayName: 'Paired Passage Short Answer',
            lessonId:
              tmap['Paired Passage SCR Template'].variants[
                'CA CAASPP ELA Short Answer Paired Passages'
              ],
          },
          {
            variant: 'paired-passage-upload',
            displayName: 'Paired Passage Short Answer',
            lessonId:
              tmap['Curify Paired Passage SCR Template'].variants[
                'CA CAASPP ELA Short Answer Paired Passage Curity'
              ],
          },
        ],
      },
      {
        id: 'PT',
        displayName: 'Performance Task',
        grades: [3, 4, 5, 6, 7, 8],
        lessonVariants: [
          {
            variant: 'informational-composition',
            displayName: 'Informational Composition',
            lessonId:
              tmap['ELA Performance Task Template'].variants[
                'CA CAASPP ELA Informational Performance Task'
              ],
          },
          {
            variant: 'opinion-composition',
            displayName: 'Opinion Composition',
            lessonId:
              tmap['ELA Performance Task Template'].variants[
                'CA CAASPP ELA Opinion Performance Task'
              ],
          },
          {
            variant: 'argumentative-composition',
            displayName: 'Argumentative Composition',
            lessonId:
              tmap['ELA Performance Task Template'].variants[
                'CA CAASPP Argumentative Performance Task'
              ],
          },
          {
            variant: 'narrative-composition',
            displayName: 'Narrative Composition',
            lessonId:
              tmap['ELA Performance Task Template'].variants[
                'CA CAASPP Narrative Performance Task'
              ],
          },
          {
            variant: 'upload',
            displayName: 'Informational',
            lessonId:
              tmap['Curify ELA Performance Task Template (2 Passages)'].variants[
                'CA CAASPP ELA Informational Performance Task Curify'
              ],
          },
        ],
      },
    ],
  },
  //*California Math
  {
    id: 'CAASPP_MATH',
    displayName: 'Math',
    testGroup: { value: 'CAASPP', displayName: 'CAASPP' },
    states: [{ displayName: 'California', value: 'CA' }],
    questionTypes: [
      {
        id: '1-point-constructed-response',
        displayName: '1-Point Constructed Response',
        grades: [3, 4, 5, 6, 7, 8],
        lessonVariants: [
          {
            variant: '1-point-constructed-response',
            displayName: '1-Point Constructed Response',
            lessonId:
              tmap['1- & 2-Point Open Response'].variants['Multi SBAC Math 1-Point'],
          },
        ],
      },
      {
        id: '2-point-constructed-response',
        displayName: '2-Point Constructed Response',
        grades: [3, 4, 5, 6, 7, 8],
        lessonVariants: [
          {
            variant: '2-point-constructed-response',
            displayName: '2-Point Constructed Response',
            lessonId:
              tmap['1- & 2-Point Open Response'].variants['CA CAASPP Math 2-Point'],
          },
        ],
      },
      {
        id: '3-point-constructed-response',
        displayName: '3-Point Constructed Response',
        grades: [3, 4, 5, 6, 7, 8],
        lessonVariants: [
          {
            variant: '3-point-constructed-response',
            displayName: '3-Point Constructed Response',
            lessonId:
              tmap['3- & 4-Point Open Response'].variants['CA CAASPP Math 3-Point'],
          },
        ],
      },
      {
        id: '4-point-constructed-response',
        displayName: '4-Point Constructed Response',
        grades: [3, 4, 5, 6, 7, 8],
        lessonVariants: [
          {
            variant: '4-point-constructed-response',
            displayName: '4-Point Constructed Response',
            lessonId: tmap['3- & 4-Point Open Response'].variants['CA CAASPP 4-Point'],
          },
        ],
      },
      {
        id: '1-point-performance-task-component',
        displayName: '1-Point Performance Task Component',
        grades: [3, 4, 5, 6, 7, 8],
        lessonVariants: [
          {
            variant: '1-point-performance-task-component',
            displayName: '1-Point Performance Task Component',
            lessonId:
              tmap['1- & 2-Point Performance Task Question'].variants[
                'CA CAASPP Math Performance Task 1-Point'
              ],
          },
        ],
      },
      {
        id: '2-point-performance-task-component',
        displayName: '2-Point Performance Task Component',
        grades: [3, 4, 5, 6, 7, 8],
        lessonVariants: [
          {
            variant: '2-point-performance-task-component',
            displayName: '2-Point Performance Task Component',
            lessonId:
              tmap['1- & 2-Point Performance Task Question'].variants[
                'CA CAASPP Math Performance Task 2-Point'
              ],
          },
        ],
      },
      {
        id: '3-point-performance-task-component',
        displayName: '3-Point Performance Task Component',
        grades: [3, 4, 5, 6, 7, 8],
        lessonVariants: [
          {
            variant: '3-point-performance-task-component',
            displayName: '3-Point Performance Task Component',
            lessonId:
              tmap['3- & 4-Point Performance Task Question'].variants[
                'CA CAASPP Performance Task 3-Point'
              ],
          },
        ],
      },
      {
        id: '4-point-performance-task-component',
        displayName: '4-Point Performance Task Component',
        grades: [3, 4, 5, 6, 7, 8],
        lessonVariants: [
          {
            variant: '4-point-performance-task-component',
            displayName: '4-Point Performance Task Component',
            lessonId:
              tmap['3- & 4-Point Performance Task Question'].variants[
                'CA CAASPP Performance Task 4-Point'
              ],
          },
        ],
      },
      {
        id: 'performance-task-simulation',
        displayName: 'Performance Task Simulation',
        grades: [3, 4, 5, 6, 7, 8],
        lessonVariants: [
          {
            variant: 'performance-task-simulation',
            displayName: 'Performance Task Simulation',
            lessonId:
              tmap['Performance Task '].variants['Multi SBAC Math Performance Task'],
          },
        ],
      },
    ],
  },
  //*California Science
  {
    id: 'CAASPP_SCI',
    displayName: 'Science',
    testGroup: { value: 'CAASPP', displayName: 'CAASPP' },
    states: [{ displayName: 'California', value: 'CA' }],
    questionTypes: [
      {
        id: 'SCR',
        displayName: 'Short Constructed Response (SCR)',
        grades: [5, 8],
        lessonVariants: [
          {
            variant: 'default',
            displayName: 'Default',
            lessonId: config.testprep.caasppScienceSCR,
          },
          {
            variant: 'upload',
            displayName: 'Upload',
            lessonId: config.testprep.caasppScienceSCRUpload,
          },
        ],
      },
    ],
  },
  //*California ELPAC
  {
    id: 'ELPAC',
    displayName: 'EL Proficiency',
    testGroup: { value: 'ELPAC', displayName: 'ELPAC' },
    states: [{ displayName: 'California', value: 'CA' }],
    questionTypes: [
      {
        id: 'write_about_an_experience',
        displayName: 'Write About an Experience',
        grades: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12],
        lessonVariants: [
          {
            variant: 'default',
            displayName: 'Default',
            lessonId: config.testprep.elpacWriteAboutAnExperience,
          },
        ],
      },
    ],
  },

  //*TEXAS STAAR
  {
    id: 'STAAR_RLA',
    displayName: 'RLA',
    testGroup: { value: 'STAAR', displayName: 'STAAR' },
    states: [{ displayName: 'Texas', value: 'TX' }],
    questionTypes: [
      {
        id: 'SCR',
        displayName: 'Short Constructed Response (SCR)',
        grades: [3, 4, 5, 6, 7, 8, 9, 10, 11, 12],
        lessonVariants: [
          {
            variant: 'short-answer',
            displayName: 'Short Answer',
            lessonId: tmap['SCR Template'].variants['TX STAAR ELA SCR'],
          },
          {
            variant: 'upload',
            displayName: 'Upload',
            lessonId: tmap['Curify SCR Template'].variants['TX STAAR ELA SCR Curify'],
          },
        ],
      },
      {
        id: 'PP',
        displayName: 'Paired Passage (SCR)',
        grades: [3, 4, 5, 6, 7, 8, 9, 10, 11, 12],
        lessonVariants: [
          {
            variant: 'paired-passage',
            displayName: 'Paired Passage',
            lessonId:
              tmap['Paired Passage SCR Template'].variants[
                'TX STAAR ELA SCR Paired Passages'
              ],
          },
          {
            variant: 'upload-paired-passage',
            displayName: 'Upload',
            lessonId:
              tmap['Curify Paired Passage SCR Template'].variants[
                'TX STAAR ELA SCR Paired Passage Curify'
              ],
          },
        ],
      },
      {
        id: 'ECR',
        displayName: 'Extended Constructed Response (ECR)',
        grades: [3, 4, 5, 6, 7, 8, 9, 10, 11, 12],
        lessonVariants: [
          {
            variant: 'informational-composition',
            displayName: 'Informational Composition',
            lessonId: tmap['ECR Template'].variants['TX STAAR ELA Informational ECR'],
          },
          {
            variant: 'argumentative-composition',
            displayName: 'Argumentative Composition',
            lessonId: tmap['ECR Template'].variants['TX STAAR ELA Argumentative ECR'],
          },
          {
            variant: 'correspondence-composition',
            displayName: 'Correspondence Composition',
            lessonId: tmap['ECR Template'].variants['TX STAAR ELA Correspondence ECR'],
          },
          {
            variant: 'upload-informational-composition',
            displayName: 'Informational Composition',
            lessonId:
              tmap['Curify ECR Template (1 Passage)'].variants[
                'TX STAAR ELA Informational ECR Curify'
              ],
          },
          {
            variant: 'upload-argumentative-composition',
            displayName: 'Argumentative Composition',
            lessonId:
              tmap['Curify ECR Template (1 Passage)'].variants[
                'TX STAAR ELA Argumentative ECR Curify'
              ],
          },
          {
            variant: 'upload-informational-correspondence',
            displayName: 'Correspondence Composition',
            lessonId:
              tmap['Curify ECR Template (1 Passage)'].variants[
                'TX STAAR ELA Correspondence ECR Curify'
              ],
          },
        ],
      },
      {
        id: 'revising_sentences',
        displayName: 'Revising Sentences (SCR)',
        grades: [3, 4, 5, 6, 7, 8, 9, 10, 11, 12],
        lessonVariants: [
          {
            variant: 'revising-sentences',
            displayName: 'Revising Sentences',
            lessonId: config.testprep.staarRLASCRRevisingSentences,
          },
        ],
      },
    ],
  },
  {
    id: 'STAAR_SS',
    displayName: 'Social Studies',
    testGroup: { value: 'STAAR', displayName: 'STAAR' },
    states: [{ displayName: 'Texas', value: 'TX' }],
    questionTypes: [
      {
        id: 'SCR',
        displayName: 'Short Constructed Response (SCR)',
        grades: [8],
        lessonVariants: [
          {
            variant: 'default',
            displayName: 'Default',
            lessonId: config.testprep.staarSocialStudiesSCR,
          },
          {
            variant: 'upload',
            displayName: 'Upload',
            lessonId: config.testprep.staarSocialStudiesSCRUpload,
          },
        ],
      },
    ],
  },
  {
    id: 'STAAR_USH',
    displayName: 'U.S. History',
    testGroup: { value: 'STAAR', displayName: 'STAAR' },
    states: [{ displayName: 'Texas', value: 'TX' }],
    questionTypes: [
      {
        id: 'SCR',
        displayName: 'Short Constructed Response (SCR)',
        grades: [10],
        lessonVariants: [
          {
            variant: 'default',
            displayName: 'Default',
            lessonId: tmap['SCR Template'].variants['TX STAAR ELA SCR'],
          },
          {
            variant: 'upload',
            displayName: 'Upload',
            lessonId: config.testprep.staarUSHistorySCRUpload,
          },
        ],
      },
    ],
  },
  {
    id: 'STAAR_SCI',
    displayName: 'Science',
    testGroup: { value: 'STAAR', displayName: 'STAAR' },
    states: [{ displayName: 'Texas', value: 'TX' }],
    questionTypes: [
      {
        id: 'SCR',
        displayName: 'Short Constructed Response (SCR)',
        grades: [5, 8],
        lessonVariants: [
          {
            variant: 'default',
            displayName: 'Default',
            lessonId: config.testprep.staarScienceSCR,
          },
          {
            variant: 'upload',
            displayName: 'Upload',
            lessonId: config.testprep.staarScienceSCRUpload,
          },
        ],
      },
    ],
  },
  {
    id: 'STAAR_BIO',
    displayName: 'Biology',
    testGroup: { value: 'STAAR', displayName: 'STAAR' },
    states: [{ displayName: 'Texas', value: 'TX' }],
    questionTypes: [
      {
        id: 'SCR',
        displayName: 'Short Constructed Response (SCR)',
        grades: [9, 10],
        lessonVariants: [
          {
            variant: 'default',
            displayName: 'Default',
            lessonId: config.testprep.staarBiologySCR,
          },
          {
            variant: 'upload',
            displayName: 'Upload',
            lessonId: config.testprep.staarBiologySCRUpload,
          },
        ],
      },
    ],
  },
  {
    id: 'STAAR_MATH',
    displayName: 'Math',
    testGroup: { value: 'STAAR', displayName: 'STAAR' },
    states: [{ displayName: 'Texas', value: 'TX' }],
    questionTypes: [
      {
        id: '1-point-constructed-response',
        displayName: '1-Point Constructed Response',
        grades: [3, 4, 5, 6, 7, 8],
        lessonVariants: [
          {
            variant: '1-point-constructed-response',
            displayName: '1-Point Constructed Response',
            lessonId:
              tmap['1- & 2-Point Open Response'].variants['TX STAAR Math 1-Point'],
          },
        ],
      },
      {
        id: '2-point-constructed-response',
        displayName: '2-Point Constructed Response',
        grades: [3, 4, 5, 6, 7, 8],
        lessonVariants: [
          {
            variant: '2-point-constructed-response',
            displayName: '2-Point Constructed Response',
            lessonId:
              tmap['1- & 2-Point Open Response'].variants['TX STAAR Math 2-Pont 2-Part'],
          },
        ],
      },
    ],
  },
  {
    id: 'STAAR_ALG',
    displayName: 'Algebra I',
    testGroup: { value: 'STAAR', displayName: 'STAAR' },
    states: [{ displayName: 'Texas', value: 'TX' }],
    questionTypes: [
      {
        id: 'text_input',
        displayName: 'Text Input',
        grades: [10],
        lessonVariants: [
          {
            variant: 'default',
            displayName: 'Default',
            lessonId: config.testprep.staarAlgebraITextInput,
          },
          {
            variant: 'upload',
            displayName: 'Upload',
            lessonId: config.testprep.staarAlgebraITextInputUpload,
          },
        ],
      },
    ],
  },
  //*TEXAS TELPAS
  {
    id: 'TELPAS',
    displayName: 'EL Proficiency',
    testGroup: { value: 'TELPAS', displayName: 'TELPAS' },
    states: [{ displayName: 'Texas', value: 'TX' }],
    questionTypes: [
      {
        id: 'tell_a_story_answer_questions',
        displayName: 'Tell a Story: Answer Questions',
        grades: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12],
        lessonVariants: [
          {
            variant: 'default',
            displayName: 'Default',
            lessonId: config.testprep.telpasTellAStoryAnswerQuestions,
          },
        ],
      },
      {
        id: 'tell_a_story_four_pictures',
        displayName: 'Tell a Story: Four Pictures',
        grades: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12],
        lessonVariants: [
          {
            variant: 'default',
            displayName: 'Default',
            lessonId: config.testprep.telpasTELLAStoryFourPictures,
          },
        ],
      },
      {
        id: 'tell_a_story_one_picture',
        displayName: 'Tell a Story: One Picture',
        grades: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12],
        lessonVariants: [
          {
            variant: 'default',
            displayName: 'Default',
            lessonId: config.testprep.telpasTELLAStoryOnePicture,
          },
        ],
      },
      {
        id: 'describe_the_pictures',
        displayName: 'Describe the Pictures',
        grades: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12],
        lessonVariants: [
          {
            variant: 'default',
            displayName: 'Default',
            lessonId: config.testprep.telpasDescribeThePictures,
          },
        ],
      },
      {
        id: 'respond_to_a_scenario',
        displayName: 'Respond to a Scenario',
        grades: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12],
        lessonVariants: [
          {
            variant: 'default',
            displayName: 'Default',
            lessonId: config.testprep.telpasRespondToAScenario,
          },
        ],
      },
      {
        id: 'write_about_bar_graph',
        displayName: 'Write About Bar Graph',
        grades: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12],
        lessonVariants: [
          {
            variant: 'default',
            displayName: 'Default',
            lessonId: config.testprep.telpasWriteAboutBarGraph,
          },
        ],
      },
    ],
  },
  //*PENNSYLVANIA PSSA
  {
    id: 'PSSA_ELA',
    displayName: 'ELA',
    testGroup: { value: 'PSSA', displayName: 'PSSA' },
    states: [{ displayName: 'Pennsylvania', value: 'PA' }],
    questionTypes: [
      {
        id: 'short_answer',
        displayName: 'Short-Answer',
        grades: [3],
        lessonVariants: [
          {
            variant: 'informational',
            displayName: 'Informational',
            lessonId: config.testprep.pssaSAInformational,
          },
          {
            variant: 'upload',
            displayName: 'Upload',
            lessonId: config.testprep.pssaSAUpload,
          },
        ],
      },
      {
        id: 'text_dependent_analysis',
        displayName: 'Text-Dependent Analysis',
        grades: [4, 5, 6, 7, 8],
        lessonVariants: [
          {
            variant: 'informational',
            displayName: 'Informational',
            lessonId: config.testprep.pssaTDAInformational,
          },
          {
            variant: 'narrative',
            displayName: 'Narrative',
            lessonId: config.testprep.pssaTDANarrative,
          },
          {
            variant: 'upload',
            displayName: 'Upload',
            lessonId: config.testprep.pssaTDAUpload,
          },
        ],
      },
    ],
  },
  {
    id: 'PSSA_SCI',
    displayName: 'Science',
    testGroup: { value: 'PSSA', displayName: 'PSSA' },
    states: [{ displayName: 'Pennsylvania', value: 'PA' }],
    questionTypes: [
      {
        id: 'Short Answer',
        displayName: 'Short-Answer',
        grades: [5, 8],
        lessonVariants: [
          {
            variant: 'default',
            displayName: 'Default',
            lessonId: config.testprep.pssaScienceShortAnswer,
          },
          // { //TODO coming soon
          //   variant: 'upload',
          //   displayName: 'Upload',
          //   lessonId: config.testprep.pssaScienceShortAnswerUpload,
          // },
        ],
      },
    ],
  },
  {
    id: 'M-STEP',
    displayName: 'ELA',
    testGroup: { value: 'M-STEP', displayName: 'M-STEP' },
    states: [{ displayName: 'Michigan', value: 'MI' }],
    questionTypes: [
      {
        id: 'passage_based_writing',
        displayName: 'Passage-Based Writing',
        grades: [3, 4, 5, 6, 7],
        lessonVariants: [
          {
            variant: 'informational_composition',
            displayName: 'Informational Composition',
            lessonId: config.testprep.mstepPBWInformationalComposition,
          },

          {
            variant: 'upload',
            displayName: 'Upload',
            lessonId: config.testprep.mstepPBWUpload,
          },
        ],
      },
    ],
  },
  // Arizona
  {
    id: 'Arizona AASA',
    displayName: 'ELA',
    testGroup: { value: 'arizona_aasa', displayName: 'Arizona AASA' },
    states: [{ displayName: 'Arizona', value: 'AZ' }],
    questionTypes: [
      {
        id: 'short_answer',
        displayName: 'Short answer',
        grades: [3, 4, 5, 6, 7, 8],
        lessonVariants: [
          {
            variant: 'short_answer',
            displayName: 'Short answer',
            lessonId: config.testprep.arizonaMasterShortAnswerArgumentative,
          },
          {
            variant: 'upload',
            displayName: 'Upload',
            lessonId: config.testprep.arizonaCurifyShortAnswer,
          },
        ],
      },
      {
        id: 'essay',
        displayName: 'Essay',
        grades: [3, 4, 5, 6, 7, 8],
        lessonVariants: [
          {
            //note to future we are splitting by "upload" prefix
            variant: 'argumentative',
            displayName: 'Opinion/Argumentative Essay',
            lessonId: config.testprep.arizonaMasterArgumentativeEssay,
          },
          {
            //note to future we are splitting by "upload" prefix
            variant: 'upload-argumentative',
            displayName: 'Opinion/Argumentative Essay',
            lessonId: config.testprep.arizonaCurifyArgumentativeEssay,
          },
          {
            //note to future we are splitting by "upload" prefix
            variant: 'informative',
            displayName: 'Informative/Explanatory Essay',
            lessonId: config.testprep.arizonaMasterInformativeEssay,
          },
          {
            //note to future we are splitting by "upload" prefix
            variant: 'upload-informative',
            displayName: 'Informative/Explanatory Essay',
            lessonId: config.testprep.arizonaCurifyInformativeEssay,
          },
        ],
      },
    ],
  },
  //*NORTH CAROLINA STATE TESTS
  {
    id: 'NC_STATE_TEST',
    displayName: 'ELA',
    testGroup: { value: 'NC', displayName: 'NC State Test' },
    states: [{ displayName: 'North Carolina', value: 'NC' }],
    questionTypes: [
      {
        id: 'constructed_response',
        displayName: 'Constructed Response',
        grades: [3, 4, 5, 6, 7, 8, 9, 10, 11, 12],
        lessonVariants: [
          {
            variant: 'informational_composition',
            displayName: 'Informational Composition',
            lessonId: config.testprep.ncStateTestCRInformationalComposition,
          },
          {
            variant: 'upload',
            displayName: 'Upload',
            lessonId: config.testprep.ncStateTestELAUpload,
          },
        ],
      },
    ],
  },
  {
    //*SBAC TESTS
    id: 'SBAC',
    displayName: 'ELA',
    testGroup: { value: 'SBAC', displayName: 'SBAC' },
    states: [
      { displayName: 'Connecticut', value: 'CT' },
      { displayName: 'Delaware', value: 'DE' },
      { displayName: 'Hawaii', value: 'HI' },
      { displayName: 'Idaho', value: 'ID' },
      { displayName: 'Maine', value: 'ME' },
      { displayName: 'Montana', value: 'MT' },
      { displayName: 'Nevada', value: 'NV' },
      { displayName: 'Oregon', value: 'OR' },
      { displayName: 'Vermont', value: 'VT' },
      { displayName: 'Washington', value: 'WA' },
    ],
    questionTypes: [
      {
        id: 'short_answer',
        displayName: 'Short Answer',
        grades: [3, 4, 5, 6, 7, 8],
        lessonVariants: [
          {
            variant: 'informational',
            displayName: 'Informational',
            lessonId: config.testprep.sbacELAShortAnswerInformational,
          },
          {
            variant: 'argumentative',
            displayName: 'Argumentative',
            lessonId: config.testprep.sbacELAShortAnswerArgumentative,
          },
          {
            variant: 'fiction',
            displayName: 'Fiction',
            lessonId: config.testprep.sbacELAShortAnswerFiction,
          },
          {
            variant: 'upload',
            displayName: 'Upload',
            lessonId: config.testprep.sbacELAShortAnswerUpload,
          },
        ],
      },
      {
        id: 'PT',
        displayName: 'Performance Task',
        grades: [3, 4, 5, 6, 7, 8],
        lessonVariants: [
          {
            variant: 'informational-composition',
            displayName: 'Informational Composition',
            lessonId: config.testprep.sbacELAPerformanceTaskInformationalComposition,
          },
          {
            variant: 'argumentative-composition',
            displayName: 'Argumentative Composition',
            lessonId: config.testprep.sbacELAPerformanceTaskArgumentativeComposition,
          },
          {
            variant: 'narrative-composition',
            displayName: 'Narrative Composition',
            lessonId: config.testprep.sbacELAPerformanceTaskNarrativeComposition,
          },
          {
            variant: 'upload-informational',
            displayName: 'Informational',
            lessonId: config.testprep.sbacELAPerformanceTaskUploadInformational,
          },
          {
            variant: 'upload-argumentative',
            displayName: 'Argumentative',
            lessonId: config.testprep.sbacELAPerformanceTaskUploadArgumentative,
          },
          {
            variant: 'upload-narrative',
            displayName: 'Narrative',
            lessonId: config.testprep.sbacELAPerformanceTaskUploadNarrative,
          },
        ],
      },
    ],
  },
  //*NEW YORK REGENTS
  {
    id: 'NY_REGENTS',
    displayName: 'ELA',
    testGroup: { value: 'NY_REGENTS', displayName: 'NYSTP' },
    states: [{ displayName: 'New York', value: 'NY' }],
    questionTypes: [
      {
        id: 'text_analysis',
        displayName: 'Text Analysis',
        grades: [3, 4, 5, 6, 7, 8, 9, 10, 11, 12],
        lessonVariants: [
          {
            variant: 'Informational',
            displayName: 'Informational',
            lessonId: config.testprep.regentsTextAnalysisInformational,
          },
        ],
      },
    ],
  },
  //*WIDA ACCESS ELLs
  {
    id: 'WIDA',
    displayName: 'EL Proficiency',
    testGroup: { value: 'WIDA', displayName: 'WIDA' },
    states: [
      { displayName: 'Alabama', value: 'AL' },
      { displayName: 'Alaska', value: 'AK' },
      { displayName: 'Colorado', value: 'CO' },
      { displayName: 'Connecticut', value: 'CT' },
      { displayName: 'Delaware', value: 'DE' },
      { displayName: 'District of Columbia', value: 'DC' },
      { displayName: 'Florida', value: 'FL' },
      { displayName: 'Georgia', value: 'GA' },
      { displayName: 'Hawaii', value: 'HI' },
      { displayName: 'Idaho', value: 'ID' },
      { displayName: 'Illinois', value: 'IL' },
      { displayName: 'Indiana', value: 'IN' },
      { displayName: 'Kentucky', value: 'KY' },
      { displayName: 'Maine', value: 'ME' },
      { displayName: 'Maryland', value: 'MD' },
      { displayName: 'Massachusetts', value: 'MA' },
      { displayName: 'Michigan', value: 'MI' },
      { displayName: 'Minnesota', value: 'MN' },
      { displayName: 'Mississippi', value: 'MS' },
      { displayName: 'Missouri', value: 'MO' },
      { displayName: 'Montana', value: 'MT' },
      { displayName: 'Nevada', value: 'NV' },
      { displayName: 'New Hampshire', value: 'NH' },
      { displayName: 'New Jersey', value: 'NJ' },
      { displayName: 'New Mexico', value: 'NM' },
      { displayName: 'North Carolina', value: 'NC' },
      { displayName: 'North Dakota', value: 'ND' },
      { displayName: 'Oklahoma', value: 'OK' },
      { displayName: 'Oregon', value: 'OR' },
      { displayName: 'Pennsylvania', value: 'PA' },
      { displayName: 'Rhode Island', value: 'RI' },
      { displayName: 'South Carolina', value: 'SC' },
      { displayName: 'South Dakota', value: 'SD' },
      { displayName: 'Tennessee', value: 'TN' },
      { displayName: 'Utah', value: 'UT' },
      { displayName: 'Vermont', value: 'VT' },
      { displayName: 'Virginia', value: 'VA' },
      { displayName: 'Washington', value: 'WA' },
      { displayName: 'Wisconsin', value: 'WI' },
      { displayName: 'Wyoming', value: 'WY' },
    ],
    questionTypes: [
      {
        id: 'tier_a_write_story',
        displayName: 'Tier A: Write A Story',
        grades: ['K', 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12],
        lessonVariants: [
          {
            variant: 'tier_a_write_story',
            displayName: 'Tier A: Write A Story',
            lessonId: config.testprep.widaTierAWriteStory,
          },
        ],
      },
      {
        id: 'tier_a_make_a_decision',
        displayName: 'Tier A: Make a Decision',
        grades: ['K', 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12],
        lessonVariants: [
          {
            variant: 'tier_a_make_a_decision',
            displayName: 'Tier A: Make a Decision',
            lessonId: config.testprep.widaTierAMakeADecision,
          },
        ],
      },
      {
        id: 'tier_b_c_step_by_step_description',
        displayName: 'Tier B/C: Step-by-Step Description',
        grades: ['K', 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12],
        lessonVariants: [
          {
            variant: 'tier_b_c_step_by_step_description',
            displayName: 'Tier B/C: Step-by-Step Description',
            lessonId: config.testprep.widaTierBCStepByStepDescription,
          },
        ],
      },
    ],
  },
  //!The following tests are not for a specific state but accross all states
  {
    id: 'RACE',
    displayName: 'ELA',
    testGroup: { value: 'RACE', displayName: 'R.A.C.E.' },
    states: [
      { displayName: 'Writing Frameworks', value: 'ALL' },
      { displayName: 'Texas', value: 'TX' },
    ],
    questionTypes: [
      {
        id: 'constructed_response',
        displayName: 'Constructed Response',
        grades: [3, 4, 5, 6, 7, 8, 9, 10, 11, 12],
        lessonVariants: [
          {
            variant: 'default',
            displayName: 'Default',
            lessonId: config.testprep.raceConstructedResponse,
          },
          {
            variant: 'upload',
            displayName: 'Upload',
            lessonId: config.testprep.raceUpload,
          },
        ],
      },
    ],
  },
];
const tests: Test[] = [
  //*CALIFORNIA CAASPP
  {
    id: 'CAASPP',
    displayName: 'ELA',
    testGroup: { value: 'CAASPP', displayName: 'CAASPP' },
    states: [{ displayName: 'California', value: 'CA' }],
    questionTypes: [
      {
        id: 'SA',
        displayName: 'Short Answer',
        grades: [3, 4, 5, 6, 7, 8],
        lessonVariants: [
          {
            variant: 'informational',
            displayName: 'Informational',
            lessonId: config.testprep.caasppELAShortAnswerInformational,
          },
          {
            variant: 'argumentative',
            displayName: 'Argumentative',
            lessonId: config.testprep.caasppELAShortAnswerArgumentative,
          },
          {
            variant: 'fiction',
            displayName: 'Fiction',
            lessonId: config.testprep.caasppELAShortAnswerFiction,
          },
          {
            variant: 'upload',
            displayName: 'Upload',
            lessonId: config.testprep.caasppELAShortAnswerUpload,
          },
        ],
      },
      {
        id: 'PT',
        displayName: 'Performance Task',
        grades: [3, 4, 5, 6, 7, 8],
        lessonVariants: [
          {
            variant: 'informational-composition',
            displayName: 'Informational Composition',
            lessonId: config.testprep.caasppELAPerformanceTaskInformationalComposition,
          },
          {
            variant: 'argumentative-composition',
            displayName: 'Argumentative Composition',
            lessonId: config.testprep.caasppELAPerformanceTaskArgumentativeComposition,
          },
          {
            variant: 'narrative-composition',
            displayName: 'Narrative Composition',
            lessonId: config.testprep.caasppELAPerformanceTaskNarrativeComposition,
          },
          {
            variant: 'upload-informational',
            displayName: 'Informational',
            lessonId: config.testprep.caasppELAPerformanceTaskUploadInformational,
          },
          {
            variant: 'upload-argumentative',
            displayName: 'Argumentative',
            lessonId: config.testprep.caasppELAPerformanceTaskUploadArgumentative,
          },
          {
            variant: 'upload-narrative',
            displayName: 'Narrative',
            lessonId: config.testprep.caasppELAPerformanceTaskUploadNarrative,
          },
        ],
      },
    ],
  },
  //*California Math
  {
    id: 'CAASPP_MATH',
    displayName: 'Math',
    testGroup: { value: 'CAASPP', displayName: 'CAASPP' },
    states: [{ displayName: 'California', value: 'CA' }],
    questionTypes: [
      {
        id: 'text_input',
        displayName: 'Text Input',
        grades: [3, 4, 5, 6, 7, 8],
        lessonVariants: [
          {
            variant: 'text_input',
            displayName: 'Default',
            lessonId: config.testprep.caasppMathTextInput,
          },
          {
            variant: 'text_input_upload',
            displayName: 'Upload',
            lessonId: config.testprep.caasppMathTextInputUpload,
          },
        ],
      },
    ],
  },
  //*California Science
  {
    id: 'CAASPP_SCI',
    displayName: 'Science',
    testGroup: { value: 'CAASPP', displayName: 'CAASPP' },
    states: [{ displayName: 'California', value: 'CA' }],
    questionTypes: [
      {
        id: 'SCR',
        displayName: 'Short Constructed Response (SCR)',
        grades: [5, 8],
        lessonVariants: [
          {
            variant: 'default',
            displayName: 'Default',
            lessonId: config.testprep.caasppScienceSCR,
          },
          {
            variant: 'upload',
            displayName: 'Upload',
            lessonId: config.testprep.caasppScienceSCRUpload,
          },
        ],
      },
    ],
  },
  //*California ELPAC
  {
    id: 'ELPAC',
    displayName: 'EL Proficiency',
    testGroup: { value: 'ELPAC', displayName: 'ELPAC' },
    states: [{ displayName: 'California', value: 'CA' }],
    questionTypes: [
      {
        id: 'write_about_an_experience',
        displayName: 'Write About an Experience',
        grades: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12],
        lessonVariants: [
          {
            variant: 'default',
            displayName: 'Default',
            lessonId: config.testprep.elpacWriteAboutAnExperience,
          },
        ],
      },
    ],
  },

  //*TEXAS STAAR
  {
    id: 'STAAR_RLA',
    displayName: 'RLA',
    testGroup: { value: 'STAAR', displayName: 'STAAR' },
    states: [{ displayName: 'Texas', value: 'TX' }],
    questionTypes: [
      {
        id: 'SCR',
        displayName: 'Short Constructed Response (SCR)',
        grades: [3, 4, 5, 6, 7, 8, 9, 10, 11, 12],
        lessonVariants: [
          {
            variant: 'argumentative',
            displayName: 'Argumentative',
            lessonId: config.testprep.staarRLASCRArgumentative,
          },
          {
            variant: 'informational',
            displayName: 'Informational',
            lessonId: config.testprep.staarRLASCRInformational,
          },
          {
            variant: 'fiction',
            displayName: 'Fiction',
            lessonId: config.testprep.staarRLASCRFiction,
          },

          {
            variant: 'upload',
            displayName: 'Upload',
            lessonId: config.testprep.staarRLASCRUpload,
          },
        ],
      },
      {
        id: 'ECR',
        displayName: 'Extended Constructed Response (ECR)',
        grades: [3, 4, 5, 6, 7, 8, 9, 10, 11, 12],
        lessonVariants: [
          {
            variant: 'informational-composition',
            displayName: 'Informational Composition',
            lessonId: config.testprep.staarRLAECRInformationalComposition,
          },
          {
            variant: 'argumentative-composition',
            displayName: 'Argumentative Composition',
            lessonId: config.testprep.staarRLAECRArgumentativeComposition,
          },
          {
            variant: 'informational-correspondence',
            displayName: 'Informational Correspondence',
            lessonId: config.testprep.staarRLAECRInformationalCorrespondence,
          },
          {
            variant: 'argumentative-correspondence',
            displayName: 'Argumentative Correspondence',
            lessonId: config.testprep.staarRLAECRArgumentativeCorrespondence,
          },
          {
            variant: 'upload-informational',
            displayName: 'Informational',
            lessonId: config.testprep.staarRLAECRUploadInformational,
          },
          {
            variant: 'upload-argumentative',
            displayName: 'Argumentative',
            lessonId: config.testprep.staarRLAECRUploadArgumentative,
          },
        ],
      },
      {
        id: 'revising_sentences',
        displayName: 'Revising Sentences (SCR)',
        grades: [3, 4, 5, 6, 7, 8, 9, 10, 11, 12],
        lessonVariants: [
          {
            variant: 'revising-sentences',
            displayName: 'Revising Sentences',
            lessonId: config.testprep.staarRLASCRRevisingSentences,
          },
        ],
      },
    ],
  },
  {
    id: 'STAAR_SS',
    displayName: 'Social Studies',
    testGroup: { value: 'STAAR', displayName: 'STAAR' },
    states: [{ displayName: 'Texas', value: 'TX' }],
    questionTypes: [
      {
        id: 'SCR',
        displayName: 'Short Constructed Response (SCR)',
        grades: [8],
        lessonVariants: [
          {
            variant: 'default',
            displayName: 'Default',
            lessonId: config.testprep.staarSocialStudiesSCR,
          },
          {
            variant: 'upload',
            displayName: 'Upload',
            lessonId: config.testprep.staarSocialStudiesSCRUpload,
          },
        ],
      },
    ],
  },
  {
    id: 'STAAR_USH',
    displayName: 'U.S. History',
    testGroup: { value: 'STAAR', displayName: 'STAAR' },
    states: [{ displayName: 'Texas', value: 'TX' }],
    questionTypes: [
      {
        id: 'SCR',
        displayName: 'Short Constructed Response (SCR)',
        grades: [10],
        lessonVariants: [
          {
            variant: 'default',
            displayName: 'Default',
            lessonId: config.testprep.staarUSHistorySCR,
          },
          {
            variant: 'upload',
            displayName: 'Upload',
            lessonId: config.testprep.staarUSHistorySCRUpload,
          },
        ],
      },
    ],
  },
  {
    id: 'STAAR_SCI',
    displayName: 'Science',
    testGroup: { value: 'STAAR', displayName: 'STAAR' },
    states: [{ displayName: 'Texas', value: 'TX' }],
    questionTypes: [
      {
        id: 'SCR',
        displayName: 'Short Constructed Response (SCR)',
        grades: [5, 8],
        lessonVariants: [
          {
            variant: 'default',
            displayName: 'Default',
            lessonId: config.testprep.staarScienceSCR,
          },
          {
            variant: 'upload',
            displayName: 'Upload',
            lessonId: config.testprep.staarScienceSCRUpload,
          },
        ],
      },
    ],
  },
  {
    id: 'STAAR_BIO',
    displayName: 'Biology',
    testGroup: { value: 'STAAR', displayName: 'STAAR' },
    states: [{ displayName: 'Texas', value: 'TX' }],
    questionTypes: [
      {
        id: 'SCR',
        displayName: 'Short Constructed Response (SCR)',
        grades: [9, 10],
        lessonVariants: [
          {
            variant: 'default',
            displayName: 'Default',
            lessonId: config.testprep.staarBiologySCR,
          },
          {
            variant: 'upload',
            displayName: 'Upload',
            lessonId: config.testprep.staarBiologySCRUpload,
          },
        ],
      },
    ],
  },
  {
    id: 'STAAR_MATH',
    displayName: 'Math',
    testGroup: { value: 'STAAR', displayName: 'STAAR' },
    states: [{ displayName: 'Texas', value: 'TX' }],
    questionTypes: [
      {
        id: 'text_input',
        displayName: 'Text Input',
        grades: [3, 4, 5, 6, 7, 8],
        lessonVariants: [
          {
            variant: 'default',
            displayName: 'Default',
            lessonId: config.testprep.staarMathTextInput,
          },
          {
            variant: 'upload',
            displayName: 'Upload',
            lessonId: config.testprep.staarMathTextInputUpload,
          },
        ],
      },
    ],
  },
  {
    id: 'STAAR_ALG',
    displayName: 'Algebra I',
    testGroup: { value: 'STAAR', displayName: 'STAAR' },
    states: [{ displayName: 'Texas', value: 'TX' }],
    questionTypes: [
      {
        id: 'text_input',
        displayName: 'Text Input',
        grades: [10],
        lessonVariants: [
          {
            variant: 'default',
            displayName: 'Default',
            lessonId: config.testprep.staarAlgebraITextInput,
          },
          {
            variant: 'upload',
            displayName: 'Upload',
            lessonId: config.testprep.staarAlgebraITextInputUpload,
          },
        ],
      },
    ],
  },
  //*TEXAS TELPAS
  {
    id: 'TELPAS',
    displayName: 'EL Proficiency',
    testGroup: { value: 'TELPAS', displayName: 'TELPAS' },
    states: [{ displayName: 'Texas', value: 'TX' }],
    questionTypes: [
      {
        id: 'tell_a_story_answer_questions',
        displayName: 'Tell a Story: Answer Questions',
        grades: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12],
        lessonVariants: [
          {
            variant: 'default',
            displayName: 'Default',
            lessonId: config.testprep.telpasTellAStoryAnswerQuestions,
          },
        ],
      },
      {
        id: 'tell_a_story_four_pictures',
        displayName: 'Tell a Story: Four Pictures',
        grades: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12],
        lessonVariants: [
          {
            variant: 'default',
            displayName: 'Default',
            lessonId: config.testprep.telpasTELLAStoryFourPictures,
          },
        ],
      },
      {
        id: 'tell_a_story_one_picture',
        displayName: 'Tell a Story: One Picture',
        grades: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12],
        lessonVariants: [
          {
            variant: 'default',
            displayName: 'Default',
            lessonId: config.testprep.telpasTELLAStoryOnePicture,
          },
        ],
      },
      {
        id: 'describe_the_pictures',
        displayName: 'Describe the Pictures',
        grades: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12],
        lessonVariants: [
          {
            variant: 'default',
            displayName: 'Default',
            lessonId: config.testprep.telpasDescribeThePictures,
          },
        ],
      },
      {
        id: 'respond_to_a_scenario',
        displayName: 'Respond to a Scenario',
        grades: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12],
        lessonVariants: [
          {
            variant: 'default',
            displayName: 'Default',
            lessonId: config.testprep.telpasRespondToAScenario,
          },
        ],
      },
      {
        id: 'write_about_bar_graph',
        displayName: 'Write About Bar Graph',
        grades: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12],
        lessonVariants: [
          {
            variant: 'default',
            displayName: 'Default',
            lessonId: config.testprep.telpasWriteAboutBarGraph,
          },
        ],
      },
    ],
  },
  //*PENNSYLVANIA PSSA
  {
    id: 'PSSA_ELA',
    displayName: 'ELA',
    testGroup: { value: 'PSSA', displayName: 'PSSA' },
    states: [{ displayName: 'Pennsylvania', value: 'PA' }],
    questionTypes: [
      {
        id: 'short_answer',
        displayName: 'Short-Answer',
        grades: [3],
        lessonVariants: [
          {
            variant: 'informational',
            displayName: 'Informational',
            lessonId: config.testprep.pssaSAInformational,
          },
          // { //TODO coming soon
          //   variant: 'narrative',
          //   displayName: 'Narrative',
          //   lessonId: config.testprep.pssaSANarrative,
          // },
          {
            variant: 'upload',
            displayName: 'Upload',
            lessonId: config.testprep.pssaSAUpload,
          },
        ],
      },
      {
        id: 'text_dependent_analysis',
        displayName: 'Text-Dependent Analysis',
        grades: [4, 5, 6, 7, 8],
        lessonVariants: [
          {
            variant: 'informational',
            displayName: 'Informational',
            lessonId: config.testprep.pssaTDAInformational,
          },
          {
            variant: 'narrative',
            displayName: 'Narrative',
            lessonId: config.testprep.pssaTDANarrative,
          },
          {
            variant: 'upload',
            displayName: 'Upload',
            lessonId: config.testprep.pssaTDAUpload,
          },
        ],
      },
    ],
  },
  {
    id: 'PSSA_SCI',
    displayName: 'Science',
    testGroup: { value: 'PSSA', displayName: 'PSSA' },
    states: [{ displayName: 'Pennsylvania', value: 'PA' }],
    questionTypes: [
      {
        id: 'Short Answer',
        displayName: 'Short-Answer',
        grades: [5, 8],
        lessonVariants: [
          {
            variant: 'default',
            displayName: 'Default',
            lessonId: config.testprep.pssaScienceShortAnswer,
          },
          // { //TODO coming soon
          //   variant: 'upload',
          //   displayName: 'Upload',
          //   lessonId: config.testprep.pssaScienceShortAnswerUpload,
          // },
        ],
      },
    ],
  },
  // { //TODO coming soon
  //   id: 'PSSA_MATH',
  //   displayName: 'Math',
  //   testGroup: { value: 'PSSA', displayName: 'PSSA' },
  //   states: [{ displayName: 'Pennsylvania', value: 'PA' }],
  //   questionTypes: [
  //     {
  //       id: 'open_ended_questions',
  //       displayName: 'Open Ended Questions',
  //       grades: [3, 4, 5, 6, 7, 8],
  //       lessonVariants: [
  //         {
  //           variant: 'default',
  //           displayName: 'Default',
  //           lessonId: config.testprep.pssaMathOpenEndedQuestions,
  //         },
  //         {
  //           variant: 'upload',
  //           displayName: 'Upload',
  //           lessonId: config.testprep.pssaMathOpenEndedQuestionsUpload,
  //         },
  //       ],
  //     },
  //   ],
  // },
  //* MICHIAGAN M-STEP
  {
    id: 'M-STEP',
    displayName: 'ELA',
    testGroup: { value: 'M-STEP', displayName: 'M-STEP' },
    states: [{ displayName: 'Michigan', value: 'MI' }],
    questionTypes: [
      {
        id: 'passage_based_writing',
        displayName: 'Passage-Based Writing',
        grades: [3, 4, 5, 6, 7],
        lessonVariants: [
          {
            variant: 'informational_composition',
            displayName: 'Informational Composition',
            lessonId: config.testprep.mstepPBWInformationalComposition,
          },

          {
            variant: 'upload',
            displayName: 'Upload',
            lessonId: config.testprep.mstepPBWUpload,
          },
        ],
      },
    ],
  },
  // Arizona
  {
    id: 'Arizona AASA',
    displayName: 'ELA',
    testGroup: { value: 'arizona_aasa', displayName: 'Arizona AASA' },
    states: [{ displayName: 'Arizona', value: 'AZ' }],
    questionTypes: [
      {
        id: 'short_answer',
        displayName: 'Short answer',
        grades: [3, 4, 5, 6, 7, 8],
        lessonVariants: [
          {
            variant: 'short_answer',
            displayName: 'Short answer',
            lessonId: config.testprep.arizonaMasterShortAnswerArgumentative,
          },
          {
            variant: 'upload',
            displayName: 'Upload',
            lessonId: config.testprep.arizonaCurifyShortAnswer,
          },
        ],
      },
      {
        id: 'essay',
        displayName: 'Essay',
        grades: [3, 4, 5, 6, 7, 8],
        lessonVariants: [
          {
            //note to future we are splitting by "upload" prefix
            variant: 'argumentative',
            displayName: 'Opinion/Argumentative Essay',
            lessonId: config.testprep.arizonaMasterArgumentativeEssay,
          },
          {
            //note to future we are splitting by "upload" prefix
            variant: 'upload-argumentative',
            displayName: 'Opinion/Argumentative Essay',
            lessonId: config.testprep.arizonaCurifyArgumentativeEssay,
          },
          {
            //note to future we are splitting by "upload" prefix
            variant: 'informative',
            displayName: 'Informative/Explanatory Essay',
            lessonId: config.testprep.arizonaMasterInformativeEssay,
          },
          {
            //note to future we are splitting by "upload" prefix
            variant: 'upload-informative',
            displayName: 'Informative/Explanatory Essay',
            lessonId: config.testprep.arizonaCurifyInformativeEssay,
          },
        ],
      },
    ],
  },
  //*NORTH CAROLINA STATE TESTS
  {
    id: 'NC_STATE_TEST',
    displayName: 'ELA',
    testGroup: { value: 'NC', displayName: 'NC State Test' },
    states: [{ displayName: 'North Carolina', value: 'NC' }],
    questionTypes: [
      {
        id: 'constructed_response',
        displayName: 'Constructed Response',
        grades: [3, 4, 5, 6, 7, 8, 9, 10, 11, 12],
        lessonVariants: [
          {
            variant: 'informational_composition',
            displayName: 'Informational Composition',
            lessonId: config.testprep.ncStateTestCRInformationalComposition,
          },
          {
            variant: 'upload',
            displayName: 'Upload',
            lessonId: config.testprep.ncStateTestELAUpload,
          },
        ],
      },
    ],
  },
  {
    //*SBAC TESTS
    id: 'SBAC',
    displayName: 'ELA',
    testGroup: { value: 'SBAC', displayName: 'SBAC' },
    states: [
      { displayName: 'Connecticut', value: 'CT' },
      { displayName: 'Delaware', value: 'DE' },
      { displayName: 'Hawaii', value: 'HI' },
      { displayName: 'Idaho', value: 'ID' },
      { displayName: 'Maine', value: 'ME' },
      { displayName: 'Montana', value: 'MT' },
      { displayName: 'Nevada', value: 'NV' },
      { displayName: 'Oregon', value: 'OR' },
      { displayName: 'Vermont', value: 'VT' },
      { displayName: 'Washington', value: 'WA' },
    ],
    questionTypes: [
      {
        id: 'short_answer',
        displayName: 'Short Answer',
        grades: [3, 4, 5, 6, 7, 8],
        lessonVariants: [
          {
            variant: 'informational',
            displayName: 'Informational',
            lessonId: config.testprep.sbacELAShortAnswerInformational,
          },
          {
            variant: 'argumentative',
            displayName: 'Argumentative',
            lessonId: config.testprep.sbacELAShortAnswerArgumentative,
          },
          {
            variant: 'fiction',
            displayName: 'Fiction',
            lessonId: config.testprep.sbacELAShortAnswerFiction,
          },
          {
            variant: 'upload',
            displayName: 'Upload',
            lessonId: config.testprep.sbacELAShortAnswerUpload,
          },
        ],
      },
      {
        id: 'PT',
        displayName: 'Performance Task',
        grades: [3, 4, 5, 6, 7, 8],
        lessonVariants: [
          {
            variant: 'informational-composition',
            displayName: 'Informational Composition',
            lessonId: config.testprep.sbacELAPerformanceTaskInformationalComposition,
          },
          {
            variant: 'argumentative-composition',
            displayName: 'Argumentative Composition',
            lessonId: config.testprep.sbacELAPerformanceTaskArgumentativeComposition,
          },
          {
            variant: 'narrative-composition',
            displayName: 'Narrative Composition',
            lessonId: config.testprep.sbacELAPerformanceTaskNarrativeComposition,
          },
          {
            variant: 'upload-informational',
            displayName: 'Informational',
            lessonId: config.testprep.sbacELAPerformanceTaskUploadInformational,
          },
          {
            variant: 'upload-argumentative',
            displayName: 'Argumentative',
            lessonId: config.testprep.sbacELAPerformanceTaskUploadArgumentative,
          },
          {
            variant: 'upload-narrative',
            displayName: 'Narrative',
            lessonId: config.testprep.sbacELAPerformanceTaskUploadNarrative,
          },
        ],
      },
    ],
  },
  //*NEW YORK REGENTS
  {
    id: 'NY_REGENTS',
    displayName: 'ELA',
    testGroup: { value: 'NY_REGENTS', displayName: 'NYSTP' },
    states: [{ displayName: 'New York', value: 'NY' }],
    questionTypes: [
      {
        id: 'text_analysis',
        displayName: 'Text Analysis',
        grades: [3, 4, 5, 6, 7, 8, 9, 10, 11, 12],
        lessonVariants: [
          {
            variant: 'Informational',
            displayName: 'Informational',
            lessonId: config.testprep.regentsTextAnalysisInformational,
          },
        ],
      },
    ],
  },
  //*WIDA ACCESS ELLs
  {
    id: 'WIDA',
    displayName: 'EL Proficiency',
    testGroup: { value: 'WIDA', displayName: 'WIDA' },
    states: [
      { displayName: 'Alabama', value: 'AL' },
      { displayName: 'Alaska', value: 'AK' },
      { displayName: 'Colorado', value: 'CO' },
      { displayName: 'Connecticut', value: 'CT' },
      { displayName: 'Delaware', value: 'DE' },
      { displayName: 'District of Columbia', value: 'DC' },
      { displayName: 'Florida', value: 'FL' },
      { displayName: 'Georgia', value: 'GA' },
      { displayName: 'Hawaii', value: 'HI' },
      { displayName: 'Idaho', value: 'ID' },
      { displayName: 'Illinois', value: 'IL' },
      { displayName: 'Indiana', value: 'IN' },
      { displayName: 'Kentucky', value: 'KY' },
      { displayName: 'Maine', value: 'ME' },
      { displayName: 'Maryland', value: 'MD' },
      { displayName: 'Massachusetts', value: 'MA' },
      { displayName: 'Michigan', value: 'MI' },
      { displayName: 'Minnesota', value: 'MN' },
      { displayName: 'Mississippi', value: 'MS' },
      { displayName: 'Missouri', value: 'MO' },
      { displayName: 'Montana', value: 'MT' },
      { displayName: 'Nevada', value: 'NV' },
      { displayName: 'New Hampshire', value: 'NH' },
      { displayName: 'New Jersey', value: 'NJ' },
      { displayName: 'New Mexico', value: 'NM' },
      { displayName: 'North Carolina', value: 'NC' },
      { displayName: 'North Dakota', value: 'ND' },
      { displayName: 'Oklahoma', value: 'OK' },
      { displayName: 'Oregon', value: 'OR' },
      { displayName: 'Pennsylvania', value: 'PA' },
      { displayName: 'Rhode Island', value: 'RI' },
      { displayName: 'South Carolina', value: 'SC' },
      { displayName: 'South Dakota', value: 'SD' },
      { displayName: 'Tennessee', value: 'TN' },
      { displayName: 'Utah', value: 'UT' },
      { displayName: 'Vermont', value: 'VT' },
      { displayName: 'Virginia', value: 'VA' },
      { displayName: 'Washington', value: 'WA' },
      { displayName: 'Wisconsin', value: 'WI' },
      { displayName: 'Wyoming', value: 'WY' },
    ],
    questionTypes: [
      {
        id: 'tier_a_write_story',
        displayName: 'Tier A: Write A Story',
        grades: ['K', 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12],
        lessonVariants: [
          {
            variant: 'tier_a_write_story',
            displayName: 'Tier A: Write A Story',
            lessonId: config.testprep.widaTierAWriteStory,
          },
        ],
      },
      {
        id: 'tier_a_make_a_decision',
        displayName: 'Tier A: Make a Decision',
        grades: ['K', 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12],
        lessonVariants: [
          {
            variant: 'tier_a_make_a_decision',
            displayName: 'Tier A: Make a Decision',
            lessonId: config.testprep.widaTierAMakeADecision,
          },
        ],
      },
      {
        id: 'tier_b_c_step_by_step_description',
        displayName: 'Tier B/C: Step-by-Step Description',
        grades: ['K', 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12],
        lessonVariants: [
          {
            variant: 'tier_b_c_step_by_step_description',
            displayName: 'Tier B/C: Step-by-Step Description',
            lessonId: config.testprep.widaTierBCStepByStepDescription,
          },
        ],
      },
    ],
  },
  //!The following tests are not for a specific state but accross all states
  {
    id: 'RACE',
    displayName: 'ELA',
    testGroup: { value: 'RACE', displayName: 'R.A.C.E.' },
    states: [
      { displayName: 'Writing Frameworks', value: 'ALL' },
      { displayName: 'Texas', value: 'TX' },
    ],
    questionTypes: [
      {
        id: 'constructed_response',
        displayName: 'Constructed Response',
        grades: [3, 4, 5, 6, 7, 8, 9, 10, 11, 12],
        lessonVariants: [
          {
            variant: 'default',
            displayName: 'Default',
            lessonId: config.testprep.raceConstructedResponse,
          },
          {
            variant: 'upload',
            displayName: 'Upload',
            lessonId: config.testprep.raceUpload,
          },
        ],
      },
    ],
  },
];

export const getTestHasUploadOptions = (
  test: string,
  questionType: string | undefined,
): boolean => {
  const testObj = tests.find(t => t.id === test);
  if (!testObj) return false;
  const questionTypeObj = testObj.questionTypes.find(q => q.id === questionType);
  if (!questionTypeObj) return false;
  return questionTypeObj.lessonVariants.some(l => l.variant.includes('upload'));
};

export function getTestHasUploadOptions_templated(
  test: string,
  questionType: string | undefined,
): boolean {
  const testObj = templatedTests.find(t => t.id === test);
  if (!testObj) return false;
  const questionTypeObj = testObj.questionTypes.find(q => q.id === questionType);
  if (!questionTypeObj) return false;
  return questionTypeObj.lessonVariants.some(l => l.variant.includes('upload'));
}

export function useAllTestsForTestGroup(
  state: string,
  testGroupValue: string | undefined,
  generators: ConsumerGenerator[] | undefined,
  userAccessBadge: UserAccessBadge | undefined,
): DropdownOption[] {
  const testPrepTree = useTestPrepTree();
  const relevantTests = testPrepTree.filter(
    t => t.testGroup.value === testGroupValue && t.states.some(s => s.value === state),
  );

  return relevantTests.map(test => {
    const hasAtLeastOneFreeGenerator = test.questionTypes.some(qType =>
      qType.lessonVariants.some(variant => {
        if (variant.variant.includes('upload')) return false;

        const foundGenerator = generators?.find(g => g.id === variant.lessonId);

        const planType: GeneratorCoarseLicenseLevel =
          foundGenerator?.aiContent?.generatorPlan ||
          (foundGenerator?.aiContent?.isPremiumGenerator
            ? GeneratorCoarseLicenseLevel.enterprise
            : GeneratorCoarseLicenseLevel.freemium);

        return planType === GeneratorCoarseLicenseLevel.freemium;
      }),
    );

    const isEnterprise = !hasAtLeastOneFreeGenerator;

    let icon: JSX.Element | undefined;
    if (isEnterprise) {
      icon = (
        <TestprepPlanIcon
          hasAccessToGenerator={
            userAccessBadge?.license.coarseLevel === LicenseCoarseLevels.enterprise
          }
          isEnterprisePlanGenerator={true}
          isPremiumPlanGenerator={false}
        />
      );
    }

    return {
      displayName: test.displayName,
      value: test.id,
      icon,
      isEnterprise,
    };
  });
}

export const getFirstTestGroupForState = (state: string): string | undefined => {
  const test = tests.find(t => t.states.some(s => s.value === state));
  return test ? test.testGroup.value : undefined;
};

export const getFirstTestGroupForState_templated = (
  state: string,
): string | undefined => {
  const test = templatedTests.find(t => t.states.some(s => s.value === state));
  return test ? test.testGroup.value : undefined;
};

export const getFirstTestForGroup = (
  state: string,
  groupValue: string,
): string | undefined => {
  const test = tests.find(
    t => t.states.some(s => s.value === state) && t.testGroup.value === groupValue,
  );
  return test ? test.id : undefined;
};

export const getFirstTestForGroup_templated = (
  state: string,
  groupValue: string,
): string | undefined => {
  const test = templatedTests.find(
    t => t.states.some(s => s.value === state) && t.testGroup.value === groupValue,
  );
  return test ? test.id : undefined;
};

export function useGroupedStatesWithTests(): {
  option: DropdownOption;
  subOptions: DropdownOption[];
}[] {
  const map = new Map<
    string,
    {
      option: DropdownOption;
      subOptions: DropdownOption[];
    }
  >();

  const testPrepTree = useTestPrepTree();

  testPrepTree.forEach(currTest => {
    currTest.states.forEach(stateObj => {
      if (!map.has(stateObj.value)) {
        map.set(stateObj.value, {
          option: { ...stateObj, displayDivider: true },
          subOptions: [],
        });
      }

      const existingGroup = map.get(stateObj.value);
      if (!existingGroup) return;

      // Check if testGroup already in subOptions
      const alreadyExists = existingGroup.subOptions.some(
        g => g.value === currTest.testGroup.value,
      );

      if (!alreadyExists) {
        existingGroup.subOptions.push({
          displayName: currTest.testGroup.displayName,
          value: currTest.testGroup.value,
        });
      }
    });
  });

  const groupedStates = Array.from(map.values());

  //Alphabetically sort the states except for All states which should appear at the end
  const alphabeticalGroupedStates = groupedStates.sort((a, b) => {
    if (a.option.value === 'ALL') return 1;
    if (b.option.value === 'ALL') return -1;
    return a.option.displayName.localeCompare(b.option.displayName);
  });

  return alphabeticalGroupedStates;
}

export const getGroupedStatesWithTests = (): {
  option: DropdownOption;
  subOptions: DropdownOption[];
}[] => {
  const map = new Map<
    string,
    {
      option: DropdownOption;
      subOptions: DropdownOption[];
    }
  >();

  tests.forEach(currTest => {
    currTest.states.forEach(stateObj => {
      if (!map.has(stateObj.value)) {
        map.set(stateObj.value, {
          option: { ...stateObj, displayDivider: true },
          subOptions: [],
        });
      }

      const existingGroup = map.get(stateObj.value);
      if (!existingGroup) return;

      // Check if testGroup already in subOptions
      const alreadyExists = existingGroup.subOptions.some(
        g => g.value === currTest.testGroup.value,
      );

      if (!alreadyExists) {
        existingGroup.subOptions.push({
          displayName: currTest.testGroup.displayName,
          value: currTest.testGroup.value,
        });
      }
    });
  });

  const groupedStates = Array.from(map.values());

  //Alphabetically sort the states except for All states which should appear at the end
  const alphabeticalGroupedStates = groupedStates.sort((a, b) => {
    if (a.option.value === 'ALL') return 1;
    if (b.option.value === 'ALL') return -1;
    return a.option.displayName.localeCompare(b.option.displayName);
  });

  return alphabeticalGroupedStates;
};

export const getQuestionTypes = ({
  testId,
  generators,
  userAccessBadge,
}: {
  testId: string | undefined;
  generators: ConsumerGenerator[] | undefined;
  userAccessBadge: UserAccessBadge | undefined;
}): DropdownOption[] => {
  const test = tests.find(t => t.id === testId);
  if (!test) return [];

  if (!generators || generators.length === 0) {
    return test.questionTypes.map(q => {
      return {
        displayName: q.displayName,
        value: q.id,
      };
    });
  }

  return test.questionTypes.map(q => {
    // 1. Determine if there's at least one FREE (non-upload) generator.
    const hasAnyFreeGenerator = q.lessonVariants.some(variant => {
      // skip if it's an "upload" variant (if that's your logic)
      if (variant.variant.includes('upload')) return false;

      // find the corresponding generator for this variant's lessonId
      const foundGenerator = generators?.find(g => g.id === variant.lessonId);

      // figure out the plan type
      const planType =
        foundGenerator?.aiContent?.generatorPlan ??
        (foundGenerator?.aiContent?.isPremiumGenerator
          ? GeneratorCoarseLicenseLevel.enterprise
          : GeneratorCoarseLicenseLevel.freemium);

      // If ANY variant is 'freemium', the question type is considered free
      return planType === GeneratorCoarseLicenseLevel.freemium;
    });

    // 2. Mark question type premium if no free generators found
    const isEnterprise = !hasAnyFreeGenerator;
    let icon: JSX.Element | undefined;

    // 3. If it's premium, attach a premium/enterprise icon
    if (isEnterprise) {
      icon = (
        <TestprepPlanIcon
          hasAccessToGenerator={
            userAccessBadge?.license.coarseLevel === LicenseCoarseLevels.enterprise
          }
          isEnterprisePlanGenerator={true}
          isPremiumPlanGenerator={false}
        />
      );
    }

    // 4. Return the dropdown option
    return {
      displayName: q.displayName,
      value: q.id,
      icon,
      isEnterprise,
    };
  });
};

export function getQuestionTypes_templated({
  testId,
  generators,
  userAccessBadge,
}: {
  testId: string | undefined;
  generators: ConsumerGenerator[] | undefined;
  userAccessBadge: UserAccessBadge | undefined;
}): DropdownOption[] {
  const test = templatedTests.find(t => t.id === testId);
  if (!test) return [];

  if (!generators || generators.length === 0) {
    return test.questionTypes.map(q => {
      return {
        displayName: q.displayName,
        value: q.id,
      };
    });
  }

  return test.questionTypes.map(q => {
    // 1. Determine if there's at least one FREE (non-upload) generator.
    const hasAnyFreeGenerator = q.lessonVariants.some(variant => {
      // skip if it's an "upload" variant (if that's your logic)
      if (variant.variant.includes('upload')) return false;

      // find the corresponding generator for this variant's lessonId
      const foundGenerator = generators?.find(g => g.id === variant.lessonId);

      // figure out the plan type
      const planType =
        foundGenerator?.aiContent?.generatorPlan ??
        (foundGenerator?.aiContent?.isPremiumGenerator
          ? GeneratorCoarseLicenseLevel.enterprise
          : GeneratorCoarseLicenseLevel.freemium);

      // If ANY variant is 'freemium', the question type is considered free
      return planType === GeneratorCoarseLicenseLevel.freemium;
    });

    // 2. Mark question type premium if no free generators found
    const isEnterprise = !hasAnyFreeGenerator;
    let icon: JSX.Element | undefined;

    // 3. If it's premium, attach a premium/enterprise icon
    if (isEnterprise) {
      icon = (
        <TestprepPlanIcon
          hasAccessToGenerator={
            userAccessBadge?.license.coarseLevel === LicenseCoarseLevels.enterprise
          }
          isEnterprisePlanGenerator={true}
          isPremiumPlanGenerator={false}
        />
      );
    }

    // 4. Return the dropdown option
    return {
      displayName: q.displayName,
      value: q.id,
      icon,
      isEnterprise,
    };
  });
}

export function useGetLessonVariants({
  testId,
  questionTypeId,
  generators,
  userAccessBadge,
  isUpload,
}: {
  testId: string | undefined;
  questionTypeId: string | undefined;
  generators: ConsumerGenerator[] | undefined;
  userAccessBadge: UserAccessBadge | undefined;
  isUpload: boolean | undefined;
}): DropdownOption[] {
  const testPrepTree = useTestPrepTree();

  return useMemo(() => {
    const test = testPrepTree.find(t => t.id === testId);
    if (!test) return [];

    const questionType = test.questionTypes.find(q => q.id === questionTypeId);
    if (!questionType) return [];

    if (!isUpload) {
      //if it is not upload we filter out any lesson variants that are not upload
      const lessonVariants = questionType.lessonVariants;
      return lessonVariants
        .map(l => {
          if (l.variant.includes('upload')) return undefined;
          const foundGenerator = generators?.find(g => g.id === l.lessonId);
          const planType =
            foundGenerator?.aiContent?.generatorPlan ||
            (foundGenerator?.aiContent?.isPremiumGenerator
              ? GeneratorCoarseLicenseLevel.enterprise
              : GeneratorCoarseLicenseLevel.freemium);

          return {
            displayName: l.displayName || l.variant,
            value: l.variant,
            icon:
              planType === 'enterprise' || planType === 'premium' ? (
                <TestprepPlanIcon
                  hasAccessToGenerator={
                    userAccessBadge?.license.coarseLevel ===
                    LicenseCoarseLevels.enterprise
                  }
                  isEnterprisePlanGenerator={
                    planType === 'enterprise' || planType === 'premium'
                  }
                  isPremiumPlanGenerator={
                    planType === 'enterprise' || planType === 'premium'
                  }
                />
              ) : (
                undefined
              ),
            isEnterprise: planType === 'enterprise' || planType === 'premium',
          };
        })
        .filter(notUndefined);
    }

    //if it is upload we filter out any lesson variants that are not upload
    const lessonVariants = questionType.lessonVariants;
    return lessonVariants
      .map(l => {
        if (!l.variant.includes('upload')) return undefined;
        const foundGenerator = generators?.find(g => g.id === l.lessonId);
        const planType =
          foundGenerator?.aiContent?.generatorPlan ||
          (foundGenerator?.aiContent?.isPremiumGenerator
            ? GeneratorCoarseLicenseLevel.enterprise
            : GeneratorCoarseLicenseLevel.freemium);
        return {
          displayName: l.displayName || l.variant,
          value: l.variant,
          icon:
            planType === 'enterprise' || planType === 'premium' ? (
              <TestprepPlanIcon
                hasAccessToGenerator={
                  userAccessBadge?.license.coarseLevel === LicenseCoarseLevels.enterprise
                }
                isEnterprisePlanGenerator={
                  planType === 'enterprise' || planType === 'premium'
                }
                isPremiumPlanGenerator={false}
              />
            ) : (
              undefined
            ),
        };
      })
      .filter(notUndefined);
  }, [testPrepTree, testId, questionTypeId, generators, userAccessBadge, isUpload]);
}

export function useGetLessonIds({
  testId,
  questionTypeId,
  lessonVariantId,
  isUpload,
}: {
  testId?: string;
  questionTypeId?: string;
  lessonVariantId?: string;
  isUpload?: boolean;
}): Array<Readonly<string>> {
  const testPrepTree = useTestPrepTree();

  return useMemo(() => {
    const test = testPrepTree.find(t => t.id === testId);
    if (!test) return [];

    let questionTypes = test.questionTypes;
    if (questionTypeId) {
      questionTypes = questionTypes.filter(q => q.id === questionTypeId);
    }

    let lessonVariants = questionTypes.flatMap(q => q.lessonVariants);

    if (lessonVariantId) {
      lessonVariants = lessonVariants.filter(v => v.variant === lessonVariantId);
    }

    if (isUpload !== undefined) {
      if (isUpload) {
        lessonVariants = lessonVariants.filter(v => v.variant.includes('upload'));
      } else {
        lessonVariants = lessonVariants.filter(v => !v.variant.includes('upload'));
      }
    }
    return [...new Set(lessonVariants.map(v => v.lessonId).filter(Boolean))];
  }, [testPrepTree, testId, questionTypeId, lessonVariantId, isUpload]);
}
